import type {
  SdkConfig,
  SdkModule,
  SdkModuleContext,
  SdkModuleExport,
} from '@ekhodealer/ekho-common';

import { dispatchErrorEvent } from './diagnostics';

export interface LoadedModule {
  module: SdkModule;
  api: Record<string, unknown> | null;
}

/** Only allow HTTPS on `*.ekho.com` (production) or localhost (dev). */
function isAllowedModuleUrl(rawUrl: string): boolean {
  try {
    const url = new URL(rawUrl, window.location.href);
    const isLocal = url.hostname === 'localhost' || url.hostname === '127.0.0.1';
    if (url.protocol !== 'https:' && !isLocal) return false;
    return isLocal || url.hostname === 'ekho.com' || url.hostname.endsWith('.ekho.com');
  } catch {
    return false;
  }
}

async function importModule(url: string): Promise<SdkModuleExport> {
  const mod = (await import(/* @vite-ignore */ url)) as Partial<SdkModuleExport>;
  if (!mod.sdkModule || typeof mod.sdkModule.init !== 'function') {
    throw new Error('module bundle missing a valid `sdkModule` export');
  }
  return { sdkModule: mod.sdkModule, api: mod.api };
}

async function importWithRetry(url: string): Promise<SdkModuleExport | null> {
  try {
    return await importModule(url);
  } catch {
    try {
      return await importModule(url);
    } catch {
      // Failed after retry — caller fires the health event.
      return null;
    }
  }
}

/** Import and init a module. Returns null if disallowed, failed, or init threw. */
export async function loadModule(
  url: string,
  config: SdkConfig,
  context: SdkModuleContext,
): Promise<LoadedModule | null> {
  if (!isAllowedModuleUrl(url)) return null;

  const loaded = await importWithRetry(url);
  if (!loaded) return null;

  try {
    await loaded.sdkModule.init(config, context);
  } catch {
    dispatchErrorEvent('module-init', loaded.sdkModule.name ?? url);
    return null;
  }
  return { module: loaded.sdkModule, api: loaded.api ?? null };
}
